import React from "react"
import { shouldReplayVideo } from "./playCount"

const HLS_MIME_TYPE = "application/vnd.apple.mpegurl"

export interface HlsVideoProps
  extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, "src"> {
  active: boolean
  fallbackUrl?: string
  maxPlayCount?: number
  preservePlaybackState?: boolean
  streamingUrl?: string
}

const HlsVideo = React.forwardRef<HTMLVideoElement, HlsVideoProps>(
  (
    {
      active,
      fallbackUrl,
      loop,
      maxPlayCount,
      onEnded,
      preservePlaybackState = false,
      streamingUrl,
      ...videoProps
    },
    forwardedRef
  ) => {
    const videoRef = React.useRef<HTMLVideoElement | null>(null)
    const completedPlayCountRef = React.useRef(0)
    const playbackPositionRef = React.useRef(0)

    const setVideoRef = React.useCallback(
      (node: HTMLVideoElement | null) => {
        videoRef.current = node

        if (typeof forwardedRef === "function") {
          forwardedRef(node)
        } else if (forwardedRef) {
          forwardedRef.current = node
        }
      },
      [forwardedRef]
    )

    React.useEffect(() => {
      completedPlayCountRef.current = 0
      playbackPositionRef.current = 0
    }, [fallbackUrl, streamingUrl])

    React.useEffect(() => {
      if (!preservePlaybackState) {
        completedPlayCountRef.current = 0
        playbackPositionRef.current = 0
      }

      const video = videoRef.current
      if (!video || !active) return

      let disposed = false
      let usingFallback = false
      let hlsInstance: { destroy: () => void } | null = null

      const tryToPlay = () => {
        if (disposed) return

        if (video.readyState >= 1 && playbackPositionRef.current > 0) {
          const latestPlayablePosition = Number.isFinite(video.duration)
            ? Math.max(video.duration - 0.05, 0)
            : playbackPositionRef.current

          video.currentTime = Math.min(
            playbackPositionRef.current,
            latestPlayablePosition
          )
        }

        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {})
        }
      }

      const useFallback = () => {
        if (disposed || usingFallback) return

        usingFallback = true
        hlsInstance?.destroy()
        hlsInstance = null
        if (!fallbackUrl) return

        video.src = fallbackUrl
        video.load()
        tryToPlay()
      }

      const handleVideoError = () => {
        useFallback()
      }

      video.addEventListener("error", handleVideoError)
      video.addEventListener("loadedmetadata", tryToPlay)

      const attachSource = async () => {
        if (!streamingUrl) {
          useFallback()
          return
        }

        try {
          const hlsModule = await import("hls.js")
          if (disposed) return

          const Hls = hlsModule.default

          if (Hls.isSupported()) {
            const hls = new Hls({
              backBufferLength: 0,
              capLevelToPlayerSize: true,
              maxBufferLength: 15,
              maxMaxBufferLength: 30,
            })

            hlsInstance = hls
            hls.on(Hls.Events.ERROR, (_event, data) => {
              if (data.fatal) useFallback()
            })
            hls.on(Hls.Events.MANIFEST_PARSED, tryToPlay)
            hls.loadSource(streamingUrl)
            hls.attachMedia(video)
            return
          }

          if (video.canPlayType(HLS_MIME_TYPE)) {
            video.src = streamingUrl
            video.load()
            tryToPlay()
            return
          }

          useFallback()
        } catch {
          useFallback()
        }
      }

      attachSource()

      return () => {
        disposed = true
        video.removeEventListener("error", handleVideoError)
        video.removeEventListener("loadedmetadata", tryToPlay)

        if (
          preservePlaybackState &&
          !video.ended &&
          Number.isFinite(video.currentTime)
        ) {
          playbackPositionRef.current = video.currentTime
        }

        hlsInstance?.destroy()
        video.pause()
        video.removeAttribute("src")
        video.load()
      }
    }, [active, fallbackUrl, preservePlaybackState, streamingUrl])

    const handleEnded = React.useCallback(
      (event: React.SyntheticEvent<HTMLVideoElement>) => {
        completedPlayCountRef.current += 1
        playbackPositionRef.current = 0
        onEnded?.(event)

        if (
          !active ||
          !shouldReplayVideo(completedPlayCountRef.current, maxPlayCount)
        ) {
          return
        }

        const video = videoRef.current
        if (!video) return

        video.currentTime = 0
        const playPromise = video.play()
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {})
        }
      },
      [active, maxPlayCount, onEnded]
    )

    return (
      <video
        ref={setVideoRef}
        loop={maxPlayCount === undefined ? loop : false}
        onEnded={handleEnded}
        {...videoProps}
      />
    )
  }
)

HlsVideo.displayName = "HlsVideo"

export default HlsVideo
